import React from 'react';
import { cn } from '../../../utils/cn';

// Button variants based on your existing patterns
const buttonVariants = {
  // Based on your .btn-primary
  primary: 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
  
  // Based on your .logout-button  
  secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 hover:text-gray-900',
  
  // Based on your .edit-button
  tertiary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 hover:text-blue-600',
  
  // Based on your .quick-toggle-button.on/.off
  success: 'bg-green-600 hover:bg-green-700 text-white shadow-sm',
  danger: 'bg-red-600 hover:bg-red-700 text-white shadow-sm',
  
  // Ghost/outline variants
  ghost: 'hover:bg-gray-100 text-gray-700',
  outline: 'border border-gray-300 hover:bg-gray-50 text-gray-700'
};

const buttonSizes = {
  sm: 'px-3 py-1.5 text-sm h-8',     // Small buttons
  base: 'px-4 py-2 text-base h-10',   // Your standard buttons
  lg: 'px-6 py-3 text-lg h-12',       // Larger buttons
  icon: 'p-2 h-10 w-10'               // Square icon buttons
};

const Button = React.forwardRef(({
  children,
  className,
  variant = 'primary',
  size = 'base',
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  ...props
}, ref) => {
  const baseStyles = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-200 cursor-pointer border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95';
  
  const variantStyles = buttonVariants[variant] || buttonVariants.primary;
  const sizeStyles = buttonSizes[size] || buttonSizes.base;
  const widthStyles = fullWidth ? 'w-full' : '';
  
  return (
    <button
      ref={ref}
      className={cn(
        baseStyles,
        variantStyles,
        sizeStyles,
        widthStyles,
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          Loading...
        </>
      ) : (
        <>
          {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;